import axios from '../lib/httpClient'

// Owns its own failure. Every caller invokes this WITHOUT awaiting it (the admin
// partial and the customer-facing reset form both call it bare), so a rejected
// promise would surface as "Uncaught (in promise)" and reach Datadog RUM as a
// spurious error every time the endpoint blips. Failing open is already the
// intent -- validatePasswordInput no-ops while passwordRules is unset, and
// Cognito enforces the policy server-side regardless -- so the catch makes the
// existing behaviour explicit rather than changing it.
window.initializePasswordRules = async (email) => {
  try {
    const { data } = await axios.get('/api/users/password_requirements', { params: { email } })
    window.passwordRules = data
  } catch (error) {
    console.error('Password policy unavailable; client-side checks disabled', error)
  }
}

// HTML5 has no cross-field constraint, so a confirmation match can only be
// expressed through setCustomValidity. Lives here rather than inline in a view
// so the rule can be shared; callers own the wiring (which elements, which
// events) because element selection is view-specific.
//
// Every caller re-checks on input to BOTH fields, and must: setCustomValidity
// is sticky, so a match cleared while typing the confirmation survives a later
// edit to the password and the field still reads valid once the pair no longer
// matches. Wiring the confirmation field alone is the bug this replaced.
window.validatePasswordConfirmation = (confirmationInput, passwordInput) => {
  if (!confirmationInput || !passwordInput) return

  // Both blank counts as matching -- that is the admin "leave the password
  // unchanged" case, and blank is enforced separately by `required` where it
  // genuinely is invalid.
  confirmationInput.setCustomValidity(
    passwordInput.value === confirmationInput.value ? '' : 'Passwords must match.',
  )
}

window.validatePasswordInput = (input) => {
  // Fail open: if the policy fetch failed we have no rules to check against,
  // and throwing here would leave the field permanently unvalidated with a
  // console error. Cognito still rejects a bad password server-side.
  if (!window.passwordRules) return

  const password = input.value

  // An empty field carries no policy error, and clearing any previous one is the
  // point: the admin edit forms treat blank as "leave the password unchanged" and
  // deliberately omit `required`, so flagging empty would make the whole form
  // unsubmittable after a type-then-clear -- blocking profile-only saves too.
  // Where blank genuinely is invalid (the customer reset form) the native
  // `required` attribute enforces it, not this function.
  if (password === '') {
    input.setCustomValidity('')
    return
  }

  let message = ''

  if (password.length < window.passwordRules.minimum_length) {
    message = `Password must be at least ${window.passwordRules.minimum_length} characters long.`
  } else if (window.passwordRules.require_uppercase && !password.match(/[A-Z]/)) {
    message = 'Password must contain at least one uppercase letter.'
  } else if (window.passwordRules.require_lowercase && !password.match(/[a-z]/)) {
    message = 'Password must contain at least one lowercase letter.'
  } else if (window.passwordRules.require_numbers && !password.match(/\d/)) {
    message = 'Password must contain at least one number.'
  } else if (window.passwordRules.require_symbols && !password.match(/[^A-Za-z0-9]/)) {
    message = 'Password must contain at least one special character.'
  }

  input.setCustomValidity(message)
}
